import React, { useEffect, useRef, useState } from "react";
import { useSiteAboutData } from "../hooks/useSiteData";
import { type } from "../lib/typography";

function useCountUp(end, duration = 1600, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    const numericEnd = parseInt(String(end).replace(/\D/g, "")) || 0;
    if (numericEnd === 0) return;
    let startTime;
    let raf;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * numericEnd));
      if (progress < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [end, duration, start]);
  return value;
}

function StatItem({ stat, visible }) {
  const parsed = parseInt(String(stat.value).replace(/\D/g, ""), 10) || 0;
  const animated = useCountUp(stat.value, 1800, visible);
  const numeric = visible ? animated : parsed;
  const suffix = String(stat.value).replace(/[0-9]/g, "");
  return (
    <div className="text-center md:text-left group">
      <div className="flex items-baseline justify-center md:justify-start gap-1">
        <span className={`${type.pageTitle} text-white`}>
          {numeric}
        </span>
        <span className={`${type.sectionTitle} text-[#aad4fa]`}>{suffix}</span>
      </div>
      <div className={`mt-2 ${type.label} text-slate-300`}>
        {stat.label}
      </div>
      <div className="mt-4 w-12 h-0.5 bg-[#aad4fa]/60 mx-auto md:mx-0 group-hover:w-20 transition-all duration-500" />
    </div>
  );
}

export default function StatsBar() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const { data: about } = useSiteAboutData();
  const stats = about?.stats || [];

  useEffect(() => {
    if (stats.length === 0 || !ref.current) return undefined;

    const el = ref.current;

    const checkVisible = () => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        setVisible(true);
      }
    };

    checkVisible();

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [stats.length]);

  if (stats.length === 0) return null;

  return (
    <section ref={ref} className="relative bg-[#0a1a2f] py-16 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#1e6fcf] blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-blue-500 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10">
        {stats.map((s) => (
          <StatItem key={s.key} stat={s} visible={visible} />
        ))}
      </div>
    </section>
  );
}
